import { paywallInterceptor } from '@/components/organisms/PaywallEmailModal/src/utils/paywallInterceptor';
import type {
	BusinessType,
	SequencesType,
	StatusType,
	StepsType
} from '@/modules/sequencesRedesign/hooks/useSequences';
import type { TemplatesType } from '@/modules/sequencesRedesign/hooks/useTemplates';
import type { ModifiedEmailsType } from '@/modules/sequencesRedesign/SequencesContext';
import service, { HttpResponseSuccess } from './service';

const apiService = service(`${process.env.REACT_BACKEND_HOST}`);

interface SequenceResponse {
	campaigns: SequencesType[];
}

export const getSequences = (): Promise<
	HttpResponseSuccess<SequenceResponse>
> => {
	return apiService.get(`/api/campaigns`);
};

interface CreateSequenceResponse {
	id: string;
	name: string;
	steps: StepsType[];
}

export const createSequence = (payload: {
	name: string;
	steps: StepsType[];
	status: StatusType;
	creationSource?: 'User' | 'AI';
}): Promise<HttpResponseSuccess<CreateSequenceResponse>> =>
	apiService.post('/api/campaigns', payload);

interface CreateAiCampaignResponse {
	name: string;
	description: string;
	isAiGenerated: boolean;
	steps: StepsType[];
}

export const createAiCampaign = (payload: {
	offering: string;
	targetAudience: string;
	valueProposition: string;
	signature?: string;
}): Promise<HttpResponseSuccess<CreateAiCampaignResponse>> =>
	apiService.post('/api/campaigns/generateAiCampaign', payload);

export const deleteSequence = (
	campaignId: string
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.delete(`/api/campaigns/${campaignId}`);

export const updateSequence = ({
	campaignId,
	...payload
}: {
	campaignId: string;
} & ({ name: string } | { status: StatusType })): Promise<
	HttpResponseSuccess<unknown>
> => apiService.put(`/api/campaigns/${campaignId}`, payload);

export const editSequenceSteps = ({
	campaignId,
	...payload
}: {
	campaignId: string;
	steps: StepsType[];
}): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post(`/api/campaigns/${campaignId}/steps`, payload);

interface SequenceTemplatesResponse {
	campaigns: TemplatesType[];
	count: number;
}

export const getSequencesTemplates = (): Promise<
	HttpResponseSuccess<SequenceTemplatesResponse>
> => {
	return apiService.get(`/api/campaigns/templates`);
};

export const createSequenceTemplate = ({
	...payload
}: TemplatesType): Promise<HttpResponseSuccess<unknown>> => {
	return apiService.post(`/api/campaigns/templates`, payload);
};

export const deleteSequenceTemplate = ({
	templateId
}: {
	templateId: string;
}): Promise<HttpResponseSuccess<unknown>> => {
	return apiService.delete(`/api/campaigns/templates/${templateId}`);
};

export const updateSequenceTemplate = ({
	templateId,
	...payload
}: {
	templateId: string;
	name: string;
	description?: string;
}): Promise<HttpResponseSuccess<unknown>> => {
	return apiService.put(`/api/campaigns/templates/${templateId}`, payload);
};

interface SequenceByIdPayload {
	campaignId: string;
	skip: number;
	limit: number;
	searchName: string;
}

interface SequenceByIdResponse {
	businesses: BusinessType[];
}

export const getBusinessesBySequenceId = ({
	campaignId,
	...payload
}: SequenceByIdPayload): Promise<HttpResponseSuccess<SequenceByIdResponse>> => {
	return apiService.get(`/api/campaigns/${campaignId}/businesses`, {
		params: payload
	});
};

export const bulkRemoveBusinessesFromSequence = ({
	campaignId,
	...payload
}: {
	campaignId: string;
	businesses: string[];
}): Promise<HttpResponseSuccess<SequenceByIdResponse>> => {
	return apiService.post(
		`/api/campaigns/${campaignId}/businesses/bulk-remove`,
		payload
	);
};

export type BusinessToIncludeType = {
	businessId: string;
	listId: string;
};

export const initiateSequence = ({
	campaignId,
	...payload
}: {
	campaignId: string;
	listIds?: string[];
	excludedBusinessIds?: string[];
	includedBusinesses?: BusinessToIncludeType[];

	modifiedEmails: ModifiedEmailsType[];
}): Promise<HttpResponseSuccess<unknown>> => {
	paywallInterceptor({ apiInstance: apiService });

	return apiService.post(`/api/campaigns/${campaignId}/initiate`, payload);
};

export interface PanelStepsType {
	stepId: string;
	subject: string;
	order?: number;
	expectedDate: string;
}

interface SequenceStepType {
	afterInterval: number;
	campaignId: string;
	name: string;
	currentStep: PanelStepsType;
	upcomingSteps: PanelStepsType[];
}

export interface BusinessSequencesType {
	businessId: string;
	campaigns: SequenceStepType[];
}

export const getBusinessSequences = (
	businessId: string
): Promise<HttpResponseSuccess<BusinessSequencesType>> => {
	return apiService.get(`/api/businesses/${businessId}/campaigns`);
};

interface BusinessesCheckResponse {
	[businessId: string]: boolean;
}

export const checkBusinessesPartOfSequence = ({
	campaignId,
	...payload
}: {
	campaignId: string;
	businessIds: string[];
}): Promise<HttpResponseSuccess<BusinessesCheckResponse>> => {
	return apiService.post(
		`/api/campaigns/${campaignId}/businesses/check`,
		payload
	);
};

export const retryInitiateSequence = ({
	campaignId
}: {
	campaignId: string;
}): Promise<HttpResponseSuccess<BusinessesCheckResponse>> => {
	return apiService.post(`/api/campaigns/${campaignId}/businesses/retry`);
};

export type RetryBusinessResponse = {
	successCount: number;
	failCount: number;
};

export const retryIndividualBusiness = ({
	campaignId,
	businessId
}: {
	campaignId: string;
	businessId: string;
}): Promise<HttpResponseSuccess<RetryBusinessResponse>> => {
	return apiService
		.post(`/api/campaigns/${campaignId}/businesses/retry`, { businessId })
		.then(response => {
			if (response.status === 200) {
				return response.data as HttpResponseSuccess<RetryBusinessResponse>;
			}
			throw response.data;
		});
};

interface SequencesAISnippetResponse {
	snippet: string;
}

export const getSequencesAISnippets = (
	payload: any
): Promise<HttpResponseSuccess<SequencesAISnippetResponse>> =>
	apiService.post('/api/campaigns/generateAISnippet', payload);
